import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SonyAdminDocumentPatchInputDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  SonyAdminDocumentDto,
  SonyObjectTypeEnum,
} from '@/UI/KnowledgeBase/SonyDocuments/SonyDocuments.dto';

const documentFieldsSchema = z.object({
  title: z.string(),
  object_type: z.nativeEnum(SonyObjectTypeEnum),
  locale: z.string(),
  view_url: z.string(),
  summary_text_content: z.string(),
  tags: z.string(),
  related_models: z.string(),
  countries: z.string(),
});

type DocumentFieldsFormValues = z.infer<typeof documentFieldsSchema>;

const toList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const toFormValues = (sonyDocument: SonyAdminDocumentDto): DocumentFieldsFormValues => ({
  title: sonyDocument.title,
  object_type: sonyDocument.object_type,
  locale: sonyDocument.locale,
  view_url: sonyDocument.view_url,
  summary_text_content: sonyDocument.summary_text_content,
  tags: sonyDocument.tags.join(', '),
  related_models: sonyDocument.related_models.join(', '),
  countries: sonyDocument.countries.join(', '),
});

interface SonyDocumentFieldsFormProps {
  externalId: string;
  sonyDocument: SonyAdminDocumentDto;
  disabled: boolean;
}

export const SonyDocumentFieldsForm = ({
  externalId,
  sonyDocument,
  disabled,
}: SonyDocumentFieldsFormProps) => {
  const { t } = useTranslation('sony_admin');
  const patchDocumentMutation = useMutation<unknown, unknown, SonyAdminDocumentPatchInputDto>({
    mutationKey: [QueryKey.sonyAdmin.documentPatch],
  });

  const initialValues = useMemo(() => toFormValues(sonyDocument), [sonyDocument]);
  const form = useForm<DocumentFieldsFormValues>({
    resolver: zodResolver(documentFieldsSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    form.reset(initialValues, { keepDirtyValues: true });
  }, [initialValues, form]);

  const onSubmit = (values: DocumentFieldsFormValues): void => {
    const { dirtyFields } = form.formState;
    const payload: SonyAdminDocumentPatchInputDto = { externalId };
    if (dirtyFields.title) payload.title = values.title;
    if (dirtyFields.object_type) payload.object_type = values.object_type;
    if (dirtyFields.locale) payload.locale = values.locale;
    if (dirtyFields.view_url) payload.view_url = values.view_url;
    if (dirtyFields.summary_text_content) {
      payload.summary_text_content = values.summary_text_content;
    }
    if (dirtyFields.tags) payload.tags = toList(values.tags);
    if (dirtyFields.related_models) payload.related_models = toList(values.related_models);
    if (dirtyFields.countries) payload.countries = toList(values.countries);
    patchDocumentMutation.mutate(payload, { onSuccess: () => form.reset(values) });
  };

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{t('sheet.document_section')}</h2>
        <p className="text-sm text-muted-foreground">{t('sheet.document_section_description')}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="sony-document-title">{t('sheet.field.title')}</Label>
          <Controller
            control={form.control}
            name="title"
            render={({ field }) => (
              <Input
                {...field}
                id="sony-document-title"
                size="sm"
                disabled={disabled}
                data-testid="sony-document-title-input"
              />
            )}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sony-document-object-type">{t('sheet.field.object_type')}</Label>
          <Controller
            control={form.control}
            name="object_type"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                <SelectTrigger
                  id="sony-document-object-type"
                  className="w-full"
                  data-testid="sony-document-object-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(SonyObjectTypeEnum).map((objectType) => (
                    <SelectItem key={objectType} value={objectType}>
                      {objectType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sony-document-locale">{t('sheet.field.locale')}</Label>
          <Controller
            control={form.control}
            name="locale"
            render={({ field }) => (
              <Input
                {...field}
                id="sony-document-locale"
                size="sm"
                disabled={disabled}
                data-testid="sony-document-locale-input"
              />
            )}
          />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="sony-document-view-url">{t('sheet.field.view_url')}</Label>
          <Controller
            control={form.control}
            name="view_url"
            render={({ field }) => (
              <Input {...field} id="sony-document-view-url" size="sm" disabled={disabled} />
            )}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sony-document-tags">{t('sheet.field.tags')}</Label>
          <Controller
            control={form.control}
            name="tags"
            render={({ field }) => (
              <Input {...field} id="sony-document-tags" size="sm" disabled={disabled} />
            )}
          />
          <p className="text-xs text-foreground-subtle">{t('sheet.field.list_hint')}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sony-document-related-models">{t('sheet.field.related_models')}</Label>
          <Controller
            control={form.control}
            name="related_models"
            render={({ field }) => (
              <Input {...field} id="sony-document-related-models" size="sm" disabled={disabled} />
            )}
          />
          <p className="text-xs text-foreground-subtle">{t('sheet.field.list_hint')}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sony-document-countries">{t('sheet.field.countries')}</Label>
          <Controller
            control={form.control}
            name="countries"
            render={({ field }) => (
              <Input {...field} id="sony-document-countries" size="sm" disabled={disabled} />
            )}
          />
          <p className="text-xs text-foreground-subtle">{t('sheet.field.list_hint')}</p>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="sony-document-summary">{t('sheet.field.summary_text_content')}</Label>
          <Controller
            control={form.control}
            name="summary_text_content"
            render={({ field }) => (
              <AutosizeTextarea
                {...field}
                id="sony-document-summary"
                minHeight={80}
                maxHeight={240}
                disabled={disabled}
                className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
            )}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={form.handleSubmit(onSubmit)}
          loading={patchDocumentMutation.isPending}
          disabled={disabled || !form.formState.isDirty}
          data-testid="sony-save-document-button"
        >
          {t('sheet.save_document')}
        </Button>
      </div>
    </section>
  );
};
